import { LayerType } from '../types/layerMetadata';
import StarIcon from '@mui/icons-material/Star';
import StyleIcon from '@mui/icons-material/Style';
import PublicIcon from '@mui/icons-material/Public';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import LayersIcon from '@mui/icons-material/Layers';
import SchoolIcon from '@mui/icons-material/School';
import GavelIcon from '@mui/icons-material/Gavel';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';

/**
 * Layer utility functions for consistent layer handling across the application
 */

export const getLayerDisplayName = (layer: LayerType): string => {
  switch (layer) {
    case 'S':
      return 'Stars';
    case 'L':
      return 'Looks';
    case 'W':
      return 'Worlds';
    case 'M':
      return 'Moves';
    case 'G':
      return 'Songs';
    case 'C':
      return 'Composite';
    case 'T':
      return 'Training Data';
    case 'R':
      return 'Rights';
    case 'P':
      return 'Personalize';
    case 'B':
      return 'Branded';
    default:
      return 'Unknown';
  }
};

export const getLayerIcon = (layer: LayerType) => {
  switch (layer) {
    case 'S':
      return StarIcon;
    case 'L':
      return StyleIcon;
    case 'W':
      return PublicIcon;
    case 'M':
      return DirectionsRunIcon;
    case 'G':
      return MusicNoteIcon;
    case 'C':
      return LayersIcon;
    case 'T':
      return SchoolIcon;
    case 'R':
      return GavelIcon;
    case 'P':
      return PersonIcon;
    case 'B':
      return BusinessIcon;
    default:
      return StarIcon;
  }
};

export const getLayerDescription = (layer: LayerType): string => {
  switch (layer) {
    case 'S':
      return 'Star performers with unique identities, styles, and personalities';
    case 'L':
      return 'Fashion styles, outfits, and aesthetic choices';
    case 'W':
      return 'Environments, locations, and atmospheric settings';
    case 'M':
      return 'Dance moves, choreography, and performance sequences';
    case 'G':
      return 'Musical tracks, songs, and audio compositions';
    case 'C':
      return 'Composite assets combining multiple layer types';
    case 'T':
      return 'Training data and machine learning datasets';
    case 'R':
      return 'Rights and licensing information';
    case 'P':
      return 'Personalized content and user-specific assets';
    case 'B':
      return 'Branded content and commercial assets';
    default:
      return 'Unknown layer type';
  }
};

export const getLayerColor = (layer: LayerType): string => {
  switch (layer) {
    case 'S':
      return '#FFD700'; // Gold
    case 'L':
      return '#FF69B4'; // Hot Pink
    case 'W':
      return '#87CEEB'; // Sky Blue
    case 'M':
      return '#98FB98'; // Pale Green
    case 'G':
      return '#9C27B0'; // Purple
    case 'C':
      return '#795548'; // Brown
    case 'T':
      return '#607D8B'; // Blue Grey
    case 'R':
      return '#F44336'; // Red
    case 'P':
      return '#00BCD4'; // Cyan
    case 'B':
      return '#3F51B5'; // Indigo
    default:
      return '#9E9E9E'; // Gray
  }
};

/**
 * Get layer-specific validation schema
 */
export const getLayerValidationSchema = (layer: LayerType) => {
  const baseSchema = {
    requiredFields: [] as string[],
    optionalFields: [] as string[],
    fieldTypes: {} as Record<string, any>,
    crossLayerDependencies: [] as any[],
  };

  switch (layer) {
    case 'S':
      return {
        ...baseSchema,
        requiredFields: ['starName', 'gender', 'ageGroup'],
        optionalFields: [
          'realName',
          'ethnicity',
          'archetype',
          'musicalStyle',
          'personalityTraits',
        ],
        fieldTypes: {
          starName: { type: 'string', minLength: 1 },
          gender: {
            type: 'enum',
            values: ['male', 'female', 'non-binary', 'unknown'],
          },
          ageGroup: {
            type: 'enum',
            values: ['child', 'teen', 'adult', 'senior'],
          },
        },
      };
    case 'L':
      return {
        ...baseSchema,
        requiredFields: ['styleCategory'],
        optionalFields: [
          'primaryGarment',
          'primaryColors',
          'formality',
          'occasion',
        ],
        fieldTypes: {
          styleCategory: { type: 'string' },
          formality: {
            type: 'enum',
            values: ['casual', 'smart-casual', 'formal', 'black-tie'],
          },
        },
      };
    case 'W':
      return {
        ...baseSchema,
        requiredFields: ['locationType'],
        optionalFields: [
          'atmosphere',
          'lightingStyle',
          'timeOfDay',
          'weatherCondition',
        ],
        fieldTypes: {
          locationType: { type: 'string' },
          timeOfDay: {
            type: 'enum',
            values: ['dawn', 'morning', 'afternoon', 'evening', 'night'],
          },
        },
      };
    case 'M':
      return {
        ...baseSchema,
        requiredFields: ['danceStyle'],
        optionalFields: ['energy', 'complexity', 'choreographyType', 'bpm'],
        fieldTypes: {
          danceStyle: { type: 'string' },
          energy: { type: 'enum', values: ['low', 'medium', 'high'] },
          complexity: {
            type: 'enum',
            values: ['beginner', 'intermediate', 'advanced', 'expert'],
          },
        },
      };
    default:
      return baseSchema;
  }
};

/**
 * Get layer-specific inheritance rules for variant assets
 */
export const getLayerInheritanceRules = (layer: LayerType) => {
  const baseRules = {
    always: [] as string[], // Always inherit from base
    if_empty: [] as string[], // Inherit only if current value is empty
    never: [] as string[], // Never inherit
    merge: [] as string[], // Merge arrays/objects
  };

  switch (layer) {
    case 'S':
      return {
        ...baseRules,
        always: ['ageGroup', 'ethnicity'],
        if_empty: ['archetype', 'careerStage', 'energy', 'vibe'],
        never: ['starName', 'hairColor', 'hairStyle', 'makeupType'],
        merge: ['musicalStyle', 'personalityTraits'],
      };
    case 'L':
      return {
        ...baseRules,
        always: ['styleCategory'],
        if_empty: ['formality', 'occasion', 'seasonality'],
        never: ['primaryGarment', 'primaryColors'],
        merge: ['accessories', 'patterns', 'materials'],
      };
    case 'W':
      return {
        ...baseRules,
        always: ['locationType'],
        if_empty: ['atmosphere', 'spatialSize'],
        never: ['lightingStyle', 'timeOfDay', 'weatherCondition'],
        merge: ['architecturalStyle', 'culturalSignificance'],
      };
    case 'M':
      return {
        ...baseRules,
        always: [],
        if_empty: ['energy', 'complexity', 'performanceLevel'],
        never: ['danceStyle', 'choreographyType', 'bpm'],
        merge: ['culturalOrigin', 'musicGenreCompatibility'],
      };
    default:
      return baseRules;
  }
};

/**
 * Get default metadata template for a layer
 */
export const getLayerDefaultMetadata = (layer: LayerType) => {
  switch (layer) {
    case 'S':
      return {
        starName: '',
        realName: '',
        gender: 'unknown',
        ageGroup: 'adult',
        ethnicity: '',
        archetype: 'performer',
        musicalStyle: [],
        personalityTraits: [],
        energy: 'medium',
        vibe: 'neutral',
        makeupType: 'natural',
        colorPalette: 'neutral',
      };
    case 'L':
      return {
        styleCategory: '',
        primaryGarment: '',
        primaryColors: [],
        formality: 'casual',
        occasion: '',
        seasonality: '',
        accessories: [],
        patterns: [],
        materials: [],
      };
    case 'W':
      return {
        locationType: '',
        atmosphere: '',
        lightingStyle: '',
        timeOfDay: '',
        weatherCondition: '',
        spatialSize: '',
        architecturalStyle: [],
        culturalSignificance: [],
      };
    case 'M':
      return {
        danceStyle: '',
        energy: 'medium',
        complexity: 'intermediate',
        choreographyType: '',
        bpm: 120,
        culturalOrigin: [],
        musicGenreCompatibility: [],
      };
    default:
      return {};
  }
};

/**
 * Check if two layer types are compatible for asset relationships
 */
export const areLayersCompatible = (
  layer1: LayerType,
  layer2: LayerType
): boolean => {
  // All layers are currently compatible with each other
  // This could be extended for more specific compatibility rules
  return true;
};

/**
 * Get processing time estimate for a layer (in milliseconds)
 */
export const getLayerProcessingEstimate = (layer: LayerType): number => {
  switch (layer) {
    case 'S':
      return 4500; // Stars layer is most complex
    case 'L':
      return 3500; // Looks layer moderate complexity
    case 'W':
      return 3000; // Worlds layer moderate complexity
    case 'M':
      return 3000; // Moves layer moderate complexity
    default:
      return 4000;
  }
};
